import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, User, FlaskConical, IndianRupee, CheckCircle, Clock, Trash2, Plus, Search } from "lucide-react";
import { toast } from 'sonner';
import { billingApi } from "@/api/billing";
import { paymentApi } from "@/api/payment";
import { labApi } from "@/api/lab";
import { BillCreateData, BillType, BillStatus, PaymentMethod, PaymentCreateData, LabTestStatus, LabOrderStatus, LabOrder } from "@/types/types";
import { useAuth } from "@/contexts/AuthContext";
import PaymentSplitsEditor, { getSplitsError, getSplitsTotal, PaymentSplitDraft } from "@/components/billing/PaymentSplitsEditor";
import { patientApi } from "@/api/patient";
import { patientNeedsRegistrationSourceCapture } from "@/lib/patientRegistrationSource";
import RegistrationSourceCaptureDialog from "@/components/patient/RegistrationSourceCaptureDialog";

type OrderTestItem = LabOrder['appointmentLabTests'][number] & {
    processingType?: 'INTERNAL' | 'EXTERNAL';
    externalCost?: number | null;
};


interface CreateLabOrderBillProps {
    labOrder: LabOrder | null;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    onTestsChanged?: () => void;
}

export default function CreateLabOrderBill({
    labOrder,
    isOpen,
    onClose,
    onSuccess,
    onTestsChanged
}: CreateLabOrderBillProps) {
    const [discountPercentage, setDiscountPercentage] = useState<number>(0);
    const [customDiscountAmount, setCustomDiscountAmount] = useState<number>(0);
    const [discountType, setDiscountType] = useState<'percentage' | 'custom'>('percentage');
    const [splits, setSplits] = useState<PaymentSplitDraft[]>([{ mode: PaymentMethod.CASH, amount: "" }]);
    const [notes, setNotes] = useState<string>("");
    const [processingStep, setProcessingStep] = useState<'form' | 'processing' | 'complete'>('form');
    const [paymentOption, setPaymentOption] = useState<'none' | 'full' | 'partial'>('none');
    const queryClient = useQueryClient();
    const { user } = useAuth();

    const [chargeOverrides, setChargeOverrides] = useState<Record<string, number>>({});
    const [orderTests, setOrderTests] = useState<OrderTestItem[]>([]);
    const [labTestSearchQuery, setLabTestSearchQuery] = useState("");
    const [showAddTests, setShowAddTests] = useState(false);
    const [addProcessingType, setAddProcessingType] = useState<"INTERNAL" | "EXTERNAL">("INTERNAL");
    const [selectedExternalVendorId, setSelectedExternalVendorId] = useState("");
    const [externalCostByLabTestId, setExternalCostByLabTestId] = useState<Record<string, string>>({});
    const [captureDialogOpen, setCaptureDialogOpen] = useState(false);

    const patientId = labOrder?.patient?.id;

    const { data: patientRecord } = useQuery({
        queryKey: ["patient", patientId],
        queryFn: () => patientApi.getPatientById(patientId!),
        enabled: !!patientId && isOpen,
    });

    const needsRegistrationSourceCapture = patientNeedsRegistrationSourceCapture(patientRecord);

    useEffect(() => {
        if (!labOrder || !isOpen) return;
        const pending = (labOrder.appointmentLabTests ?? []).filter(
            (t) => t.status === LabTestStatus.PENDING || t.status === 'PENDING'
        );
        setOrderTests(pending);
        const next: Record<string, number> = {};
        for (const t of pending) {
            next[t.id] = Number(t.labTest.charge) || 0;
        }
        setChargeOverrides(next);
        setLabTestSearchQuery("");
        setShowAddTests(false);
        setAddProcessingType("INTERNAL");
        setSelectedExternalVendorId("");
        setExternalCostByLabTestId({});
    }, [labOrder?.id, isOpen]);

    const { data: externalVendors = [] } = useQuery({
        queryKey: ['lab-external-vendors'],
        queryFn: async () => {
            const res = await labApi.listExternalVendors();
            return res.data?.data ?? [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000,
    });

    const { data: labTestsCatalogue = [] } = useQuery({
        queryKey: ['lab-tests-catalogue'],
        queryFn: async () => {
            const response = await labApi.getLabTests();
            return response.data?.data ?? [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000,
    });

    const invalidateLabOrders = () => {
        queryClient.invalidateQueries({ queryKey: ['allLabOrders'] });
        queryClient.invalidateQueries({ queryKey: ['external-lab-orders'] });
        onTestsChanged?.();
    };

    const removeTestMutation = useMutation({
        mutationFn: (appointmentLabTestId: string) => labApi.cancelLabTestOrder(appointmentLabTestId),
        onSuccess: (_data, appointmentLabTestId) => {
            setOrderTests((prev) => prev.filter((t) => t.id !== appointmentLabTestId));
            setChargeOverrides((prev) => {
                const next = { ...prev };
                delete next[appointmentLabTestId];
                return next;
            });
            invalidateLabOrders();
            toast.success('Test removed from order');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to remove test');
        },
    });

    const addTestMutation = useMutation({
        mutationFn: async (params: {
            labTestId: string;
            processingType: 'INTERNAL' | 'EXTERNAL';
            externalVendorId?: string;
            externalCost?: number;
        }) => {
            if (!labOrder) throw new Error('No lab order');
            const response = await labApi.orderLabTest({
                labTestId: params.labTestId,
                labOrderId: labOrder.id,
                appointmentId: labOrder.appointmentId,
                patientId: labOrder.patient?.id,
                processingType: params.processingType,
                externalVendorId: params.externalVendorId,
                externalCost: params.externalCost,
            });
            return response.data?.data as OrderTestItem;
        },
        onSuccess: (newTest) => {
            if (!newTest?.id) return;
            setOrderTests((prev) => [...prev, newTest]);
            setChargeOverrides((prev) => ({
                ...prev,
                [newTest.id]: Number(newTest.labTest?.charge) || 0,
            }));
            setExternalCostByLabTestId((prev) => {
                const next = { ...prev };
                delete next[newTest.labTest?.id ?? ''];
                return next;
            });
            invalidateLabOrders();
            toast.success(`Added ${newTest.labTest?.name ?? 'test'}`);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to add test');
        },
    });

    const handleAddTest = (labTestId: string) => {
        if (addProcessingType === 'EXTERNAL') {
            if (!selectedExternalVendorId) {
                toast.error('Select an external vendor');
                return;
            }
            const raw = externalCostByLabTestId[labTestId];
            const val = raw === '' || raw === undefined ? NaN : Number(raw);
            if (!Number.isFinite(val) || val < 0) {
                toast.error('Enter valid hospital cost for this external test');
                return;
            }
            addTestMutation.mutate({
                labTestId,
                processingType: 'EXTERNAL',
                externalVendorId: selectedExternalVendorId,
                externalCost: val,
            });
            return;
        }
        addTestMutation.mutate({ labTestId, processingType: 'INTERNAL' });
    };

    const availableTestsToAdd = useMemo(() => {
        const onOrder = new Set(orderTests.map((t) => t.labTest?.id));
        const q = labTestSearchQuery.trim().toLowerCase();
        return (labTestsCatalogue as Array<{ id: string; name: string; code: string; sampleType?: string; charge: number }>)
            .filter((test) => !onOrder.has(test.id))
            .filter((test) => {
                if (!q) return true;
                return (
                    test.name.toLowerCase().includes(q) ||
                    test.code.toLowerCase().includes(q) ||
                    String(test.sampleType ?? '').toLowerCase().includes(q)
                );
            });
    }, [labTestsCatalogue, orderTests, labTestSearchQuery]);

    const getTestCharge = (testId: string, fallback: number) => {
        const v = chargeOverrides[testId];
        return Number.isFinite(v) ? v : fallback;
    };

    // Calculate base price from editable charges
    const basePrice =
        orderTests.reduce((total, test) => total + (getTestCharge(test.id, test.labTest.charge) || 0), 0);

    const changeStatus = async () => {
        if (!orderTests.length) {
            console.error('No lab tests found to update status');
            return;
        }

        try {
            if (!labOrder) return;
            await labApi.updateLabOrder(labOrder.id, { status: LabOrderStatus.PROCESSING });
            const updatePromises = orderTests.map(async (test) => {
                if (test.status === LabTestStatus.PENDING || test.status === 'PENDING') {
                    return await labApi.updateLabTestOrder(test.id, { status: LabTestStatus.PROCESSING });
                }
                return null;
            });

            await Promise.all(updatePromises);
        } catch (error) {
            console.error('Error updating lab test statuses:', error);
            toast.error('Failed to update lab test statuses');
        }
    };


    // Process payment mutation
    const processPaymentMutation = useMutation({
        mutationFn: async (payments: PaymentCreateData[]) => paymentApi.bulkProcessPayments(payments),
        onError: (error: any) => {
            console.error('Error processing payment:', error);
            toast.error(error.response?.data?.message || 'Failed to process payment');
            setProcessingStep('form');
        },
    });

    // Create bill mutation
    const createBillMutation = useMutation({
        mutationFn: async (billData: BillCreateData) => {
            return await billingApi.createBill(billData);
        },
        onSuccess: () => {
            invalidateLabOrders();
            queryClient.invalidateQueries({ queryKey: ['bills'] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            toast.success('Bill created and payment processed successfully');
            changeStatus();
            setProcessingStep('complete');
            onSuccess();
            onClose();
            resetForm();
        },
        onError: (error: any) => {
            console.error('Error creating bill:', error);
            toast.error(error.response?.data?.message || 'Failed to create bill');
            setProcessingStep('form');
        },
    });

    const resetForm = () => {
        setDiscountPercentage(0);
        setCustomDiscountAmount(0);
        setDiscountType('percentage');
        setSplits([{ mode: PaymentMethod.CASH, amount: "" }]);
        setNotes("");
        setProcessingStep('form');
        setPaymentOption('none');
    };

    const handleViewBill = async () => {
        if (!labOrder?.billId) {
            // If no billId in labOrder, try to find the bill by lab order
            try {
                await billingApi.getBillsByHospital({
                    // Add any relevant filters to find the bill for this lab order
                });

                // For now, open bills in a new tab or navigate
                console.log('Finding bill for lab order:', labOrder?.id);
                toast.info('Opening bill view...');

                // You can implement navigation to bill view page here
                // Example: navigate(`/bills/${billId}`)

            } catch (error) {
                console.error('Error finding bill:', error);
                toast.error('Could not find associated bill');
            }
        } else {
            // If billId exists, navigate directly
            console.log('Viewing bill:', labOrder.billId);
            toast.info('Opening bill view...');
            // Example: navigate(`/bills/${labOrder.billId}`)
        }
    };

    const amountPaid = useMemo(() => (paymentOption === "none" ? 0 : getSplitsTotal(splits)), [paymentOption, splits]);

    const handleSubmit = async () => {
        if (!labOrder) {
            toast.error('No lab order selected');
            return;
        }

        if (needsRegistrationSourceCapture) {
            setCaptureDialogOpen(true);
            return;
        }

        await processLabBill();
    };

    const processLabBill = async () => {
        if (!labOrder) {
            return;
        }

        if (!orderTests.length) {
            toast.error('Add at least one lab test to bill');
            return;
        }

        if (!labOrder.patient?.id) {
            toast.error('Missing patient details');
            return;
        }

        const hasNegativeCharge = orderTests.some((t) => {
            const v = getTestCharge(t.id, t.labTest.charge);
            return Number.isFinite(v) && v < 0;
        });
        if (hasNegativeCharge) {
            toast.error('Test amount cannot be negative');
            return;
        }

        if (basePrice <= 0) {
            toast.error('Lab test charges must be greater than 0');
            return;
        }

        // Calculate discount amount
        let discountAmount = 0;
        if (discountType === 'percentage' && discountPercentage > 0) {
            discountAmount = (basePrice * discountPercentage) / 100;
        } else if (discountType === 'custom' && customDiscountAmount > 0) {
            discountAmount = customDiscountAmount;
        }

        if (discountAmount > basePrice) {
            discountAmount = basePrice;
        }

        const totalAmount = basePrice - discountAmount;

        if (totalAmount <= 0) {
            toast.error('Total amount must be greater than 0');
            return;
        }

        if (paymentOption === "none") {
            toast.error("Select a payment option");
            return;
        }

        const splitsErr = getSplitsError({ splits, maxTotal: totalAmount });
        if (splitsErr) {
            toast.error(splitsErr);
            return;
        }

        setProcessingStep('processing');

        try {
            const hospitalId = user?.hospitalId || '';

            const billData: BillCreateData = {
                patientId: labOrder.patient.id,
                hospitalId: hospitalId,
                appointmentId: undefined,
                paidAmount: 0,
                dueAmount: Number(totalAmount),
                status: BillStatus.DRAFT,
                billDate: new Date(),
                dueDate: new Date(),
                items: orderTests.map(test => {
                    const testCharge = getTestCharge(test.id, test.labTest.charge);
                    return ({
                    itemType: BillType.LAB_TEST,
                    description: `Lab Test - ${test.labTest.name} (${test.labTest.code})`,
                    quantity: 1,
                    unitPrice: Number(testCharge),
                    totalPrice: Number(testCharge),
                    discountAmount: Number(((Number(testCharge) || 0) / (basePrice || 1)) * discountAmount),
                    labTestId: test.id,
                    notes: notes || undefined,
                    });
                }),
                notes: notes || undefined,
            };

            console.log("billData", billData);

            const billResponse = await createBillMutation.mutateAsync(billData);
            const billId = billResponse.data?.data?.id;

            if (!billId) {
                throw new Error('Failed to create bill');
            }

            // Update lab order with billId
            try {
                console.log('Updating lab order with billId:', billId);
                await labApi.updateLabOrder(labOrder.id, { billId: billId });
                console.log('Successfully updated lab order with billId');
            } catch (updateError) {
                console.error('Failed to update lab order with billId:', updateError);
                // Don't throw error here - bill creation was successful
                toast.error('Bill created successfully, but failed to link to lab order');
            }

            if (amountPaid > 0) {
                const payments: PaymentCreateData[] = splits.map((sp) => ({
                    billId: billId,
                    amount: Number(sp.amount),
                    paymentMethod: sp.mode,
                    notes: notes || undefined,
                }));
                await processPaymentMutation.mutateAsync(payments);
            }

            // Update lab test statuses to PROCESSING after successful bill creation
            await changeStatus();
            console.log("Bill id after status update is ", billId);


        } catch (error) {
            console.error('Error in bill creation process:', error);
            toast.error('Failed to complete the billing process');
            setProcessingStep('form');
        }
    };

    const isLoading = createBillMutation.isPending || processPaymentMutation.isPending;
    const isModifyingTests = removeTestMutation.isPending || addTestMutation.isPending;

    // Calculate values
    const discountAmount = discountType === 'percentage' && discountPercentage > 0
        ? (basePrice * discountPercentage) / 100
        : discountType === 'custom' && customDiscountAmount > 0
            ? customDiscountAmount
            : 0;
    const totalAmount = basePrice - discountAmount;
    const amountDue = totalAmount - amountPaid;

    // Early return after all hooks are declared
    if (!labOrder || !isOpen) {
        return null;
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle className="text-lg font-medium">
                            {labOrder?.status === LabOrderStatus.PROCESSING ? 'Lab Bill - View' : 'Create Lab Bill'}
                        </DialogTitle>
                        {labOrder?.status && (
                            <div className="flex items-center gap-2">
                                {labOrder.status === LabOrderStatus.PENDING && (
                                    <>
                                        <Clock className="h-4 w-4 text-yellow-500" />
                                        <span className="text-sm px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium">
                                            Pending
                                        </span>
                                    </>
                                )}
                                {labOrder.status === LabOrderStatus.PROCESSING && (
                                    <>
                                        <CheckCircle className="h-4 w-4 text-blue-500" />
                                        <span className="text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                                            Processing
                                        </span>
                                    </>
                                )}
                                {labOrder.status === LabOrderStatus.COMPLETED && (
                                    <>
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                        <span className="text-sm px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                                            Completed
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        <span className="text-sm text-gray-600">
                            {processingStep === 'processing' ? 'Processing...' : 'Loading...'}
                        </span>
                    </div>
                ) : processingStep === 'complete' ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                            <IndianRupee className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="text-base font-medium text-gray-900 mb-1">Success!</h3>
                        <p className="text-sm text-gray-500 text-center">
                            Lab bill created and payment processed.
                        </p>
                    </div>
                ) : labOrder?.status === LabOrderStatus.PROCESSING ? (
                    <div className="space-y-6">
                        {/* Patient & Lab Tests Info - Read Only */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <User className="h-4 w-4 text-gray-400" />
                                    <div>
                                        <div className="font-medium text-sm">{labOrder.patient.name}</div>
                                        <div className="text-xs text-gray-500">
                                            ID: {labOrder.patient.patientUniqueId}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FlaskConical className="h-4 w-4 text-gray-400" />
                                    <div className="text-right">
                                        <div className="font-medium text-sm">{orderTests.length} Tests</div>
                                        <div className="text-xs text-gray-500">Lab Order</div>
                                    </div>
                                </div>
                            </div>

                            {/* Lab Tests List - Read Only */}
                            <div className="bg-white border rounded-lg p-4">
                                <h4 className="font-medium text-sm mb-2">Lab Tests:</h4>
                                <div className="space-y-2">
                                    {orderTests.map((test) => (
                                        <div key={test.id} className="flex justify-between items-center text-sm">
                                            <div>
                                                <span className="font-medium">{test.labTest.name}</span>
                                                <span className="text-gray-500 ml-2">({test.labTest.code})</span>
                                            </div>
                                            <span className="font-medium">₹{test.labTest.charge}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Status Message */}
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-blue-600" />
                                <div>
                                    <h3 className="font-medium text-blue-900">Lab Order is Processing</h3>
                                    <p className="text-sm text-blue-700 mt-1">
                                        Bill has been generated and the lab tests are currently being processed.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Actions for Processing Status */}
                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                className="flex-1"
                            >
                                Close
                            </Button>
                            <Button
                                type="button"
                                onClick={handleViewBill}
                                className="flex-1"
                                variant="default"
                            >
                                View Bill
                            </Button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-6">
                        {/* Patient & Lab Tests Info */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <User className="h-4 w-4 text-gray-400" />
                                    <div>
                                        <div className="font-medium text-sm">{labOrder.patient.name}</div>
                                        <div className="text-xs text-gray-500">
                                            ID: {labOrder.patient.patientUniqueId}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FlaskConical className="h-4 w-4 text-gray-400" />
                                    <div className="text-right">
                                        <div className="font-medium text-sm">{orderTests.length} Tests</div>
                                        <div className="text-xs text-gray-500">Lab Order</div>
                                    </div>
                                </div>
                            </div>

                            {/* Lab Tests List */}
                            <div className="bg-white border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm">Lab Tests:</h4>
                                    <span className="text-xs text-gray-500">Add, remove, or edit amount before billing</span>
                                </div>
                                {orderTests.length === 0 ? (
                                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mb-3">
                                        No tests on this order. Add tests below before generating the bill.
                                    </p>
                                ) : (
                                    <div className="space-y-2 mb-3">
                                        {orderTests.map((test) => (
                                            <div key={test.id} className="flex justify-between items-center text-sm gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <span className="font-medium">{test.labTest.name}</span>
                                                    <span className="text-gray-500 ml-2">({test.labTest.code})</span>
                                                    {test.processingType === 'EXTERNAL' || test.isSentExternal ? (
                                                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">External</span>
                                                    ) : (
                                                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Internal</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <span className="text-xs text-gray-500">₹</span>
                                                    <Input
                                                        type="number"
                                                        inputMode="decimal"
                                                        step="0.01"
                                                        min="0"
                                                        className="h-8 w-28 text-sm"
                                                        value={getTestCharge(test.id, test.labTest.charge)}
                                                        onChange={(e) => {
                                                            const next = Number(e.target.value);
                                                            setChargeOverrides((prev) => ({ ...prev, [test.id]: next }));
                                                        }}
                                                        disabled={createBillMutation.isPending || isModifyingTests}
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => removeTestMutation.mutate(test.id)}
                                                        disabled={createBillMutation.isPending || isModifyingTests || orderTests.length <= 1}
                                                        title={orderTests.length <= 1 ? 'At least one test is required' : 'Remove test'}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="border-t pt-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="mb-2"
                                        onClick={() => setShowAddTests((v) => !v)}
                                        disabled={createBillMutation.isPending || isModifyingTests}
                                    >
                                        <Plus className="h-4 w-4 mr-1" />
                                        {showAddTests ? 'Hide test catalogue' : 'Add tests'}
                                    </Button>
                                    {showAddTests && (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border">
                                                <div>
                                                    <Label className="text-xs font-medium text-gray-700">Processing type</Label>
                                                    <Select
                                                        value={addProcessingType}
                                                        onValueChange={(v: 'INTERNAL' | 'EXTERNAL') => setAddProcessingType(v)}
                                                    >
                                                        <SelectTrigger className="mt-1 h-9">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="INTERNAL">Internal (in-house)</SelectItem>
                                                            <SelectItem value="EXTERNAL">External (send-out)</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                {addProcessingType === 'EXTERNAL' && (
                                                    <div>
                                                        <Label className="text-xs font-medium text-gray-700">External vendor</Label>
                                                        <Select
                                                            value={selectedExternalVendorId}
                                                            onValueChange={setSelectedExternalVendorId}
                                                        >
                                                            <SelectTrigger className="mt-1 h-9">
                                                                <SelectValue placeholder="Select vendor" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {externalVendors.map((v: { id: string; name: string }) => (
                                                                    <SelectItem key={v.id} value={v.id}>
                                                                        {v.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                                                <Input
                                                    placeholder="Search tests by name, code or sample..."
                                                    value={labTestSearchQuery}
                                                    onChange={(e) => setLabTestSearchQuery(e.target.value)}
                                                    className="pl-8 h-9 text-sm"
                                                    disabled={isModifyingTests}
                                                />
                                            </div>
                                            <div className="border rounded-md max-h-48 overflow-y-auto">
                                                {availableTestsToAdd.length === 0 ? (
                                                    <div className="p-3 text-sm text-gray-500 text-center">
                                                        {labTestSearchQuery ? 'No matching tests' : 'All catalogue tests are already on this order'}
                                                    </div>
                                                ) : (
                                                    availableTestsToAdd.map((test) => (
                                                        <div
                                                            key={test.id}
                                                            className="p-3 border-b last:border-b-0 hover:bg-gray-50 flex items-center justify-between gap-2"
                                                        >
                                                            <div className="min-w-0 flex-1">
                                                                <div className="font-medium text-sm">{test.name}</div>
                                                                <div className="text-xs text-gray-500">
                                                                    {test.code} · {test.sampleType || '—'} · Patient charge ₹{test.charge}
                                                                </div>
                                                            </div>
                                                            {addProcessingType === 'EXTERNAL' && (
                                                                <div className="w-32 shrink-0">
                                                                    <Label className="text-xs text-gray-500">Hospital cost (₹)</Label>
                                                                    <Input
                                                                        inputMode="decimal"
                                                                        placeholder="0"
                                                                        value={externalCostByLabTestId[test.id] ?? ''}
                                                                        onChange={(e) =>
                                                                            setExternalCostByLabTestId((p) => ({
                                                                                ...p,
                                                                                [test.id]: e.target.value,
                                                                            }))
                                                                        }
                                                                        className="mt-1 h-8 text-sm text-right"
                                                                        disabled={isModifyingTests}
                                                                    />
                                                                </div>
                                                            )}
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="icon"
                                                                className="h-8 w-8 shrink-0"
                                                                onClick={() => handleAddTest(test.id)}
                                                                disabled={addTestMutation.isPending}
                                                                title="Add test"
                                                            >
                                                                <Plus className="h-4 w-4 text-blue-600" />
                                                            </Button>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Discount Section */}
                        <div className="bg-white border rounded-xl shadow-sm p-6 mb-6">
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Apply Discount</h3>
                                <div className="flex gap-6 mb-4">
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={discountType === 'percentage'}
                                            onChange={() => setDiscountType('percentage')}
                                            className="accent-blue-600"
                                        />
                                        Percentage (%)
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={discountType === 'custom'}
                                            onChange={() => setDiscountType('custom')}
                                            className="accent-blue-600"
                                        />
                                        Custom Amount
                                    </label>
                                </div>
                                {discountType === 'percentage' ? (
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1" htmlFor="discount-percentage">
                                            Discount Percentage
                                        </label>
                                        <Input
                                            id="discount-percentage"
                                            type="number"
                                            placeholder="e.g. 10"
                                            min="0"
                                            max="100"
                                            value={discountPercentage}
                                            onChange={e => setDiscountPercentage(Number(e.target.value))}
                                            className={`text-sm h-9 border-gray-300 rounded-md ${discountPercentage < 0 || discountPercentage > 100 ? 'border-red-500' : ''}`}
                                            disabled={createBillMutation.isPending}
                                        />
                                        <span className="text-xs text-gray-400">Enter a value between 0 and 100</span>
                                    </div>
                                ) : (
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1" htmlFor="custom-discount">
                                            Custom Discount Amount
                                        </label>
                                        <Input
                                            id="custom-discount"
                                            type="number"
                                            placeholder={`Max: ₹${basePrice}`}
                                            min="0"
                                            max={basePrice}
                                            value={customDiscountAmount}
                                            onChange={e => setCustomDiscountAmount(Number(e.target.value))}
                                            className={`text-sm h-9 border-gray-300 rounded-md ${customDiscountAmount < 0 || customDiscountAmount > basePrice ? 'border-red-500' : ''}`}
                                            disabled={createBillMutation.isPending}
                                        />
                                        <span className="text-xs text-gray-400">Enter a value up to the total fee</span>
                                    </div>
                                )}
                            </div>

                            {/* Price Summary */}
                            <div className="bg-gray-50 rounded-lg p-4 mt-4">
                                <div className="flex justify-between text-sm mb-1">
                                    <span>Base Price</span>
                                    <span>₹{basePrice.toFixed(2)}</span>
                                </div>
                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-sm text-green-600 mb-1">
                                        <span>Discount</span>
                                        <span>-₹{discountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-lg font-bold mt-2">
                                    <span>Total</span>
                                    <span>₹{totalAmount.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Payment Details */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Payment Details</Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <div className="flex items-center gap-6 mb-2">
                                        <div>
                                            <Label className="text-xs text-gray-500">Total</Label>
                                            <div className="text-lg font-semibold">₹{totalAmount.toFixed(2)}</div>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-gray-500">Due</Label>
                                            <div className={`text-lg font-semibold ${amountDue > 0 ? 'text-red-600' : 'text-green-600'}`}>₹{amountDue.toFixed(2)}</div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant={paymentOption === 'full' ? 'default' : 'outline'}
                                                className="flex-1"
                                                onClick={() => {
                                                    setPaymentOption('full');
                                                    setSplits([{ mode: PaymentMethod.CASH, amount: String(totalAmount) }]);
                                                }}
                                                disabled={createBillMutation.isPending}
                                            >
                                                Pay Full Amount
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={paymentOption === 'partial' ? 'default' : 'outline'}
                                                className="flex-1"
                                                onClick={() => {
                                                    setPaymentOption('partial');
                                                    setSplits([{ mode: PaymentMethod.CASH, amount: "" }]);
                                                }}
                                                disabled={createBillMutation.isPending}
                                            >
                                                Pay Partial Amount
                                            </Button>
                                        </div>
                                        {paymentOption !== 'none' && (
                                            <PaymentSplitsEditor
                                                splits={splits}
                                                onChange={setSplits}
                                                maxTotal={totalAmount}
                                                disabled={createBillMutation.isPending}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Notes (Optional)</Label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Additional notes..."
                                className="w-full h-20 p-2 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                                disabled={createBillMutation.isPending}
                            />
                        </div>

                        {/* Summary */}
                        <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                            <div className="flex justify-between text-sm">
                                <span>Lab Tests Total:</span>
                                <span>₹{basePrice.toFixed(2)}</span>
                            </div>
                            {discountAmount > 0 && (
                                <div className="flex justify-between text-sm text-green-600">
                                    <span>Discount:</span>
                                    <span>-₹{discountAmount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="border-t pt-2 flex justify-between font-semibold">
                                <span>Total:</span>
                                <span>₹{totalAmount.toFixed(2)}</span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={createBillMutation.isPending}
                                className="flex-1"
                            >
                                Cancel
                            </Button>

                            {/* Submit button for PENDING status only */}
                            <Button
                                type="submit"
                                disabled={
                                    !orderTests.length ||
                                    !basePrice ||
                                    basePrice <= 0 ||
                                    createBillMutation.isPending ||
                                    isModifyingTests ||
                                    paymentOption === 'none' ||
                                    !!getSplitsError({ splits, maxTotal: totalAmount })
                                }
                                className="flex-1"
                            >
                                {createBillMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Processing
                                    </>
                                ) : (
                                    'Generate Bill'
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
            {patientId ? (
                <RegistrationSourceCaptureDialog
                    open={captureDialogOpen}
                    onOpenChange={setCaptureDialogOpen}
                    patientId={patientId}
                    patientName={labOrder?.patient?.name}
                    onCaptured={() => {
                        void queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
                        void processLabBill();
                    }}
                />
            ) : null}
        </Dialog>
    );
} 